import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormBuilder } from '@kevlarcon/formkit';
import '../../styles/formkitTheme.css';
import { useQuery } from '@tanstack/react-query';
import AdminLayout from '../../components/AdminLayout';
import { formKitClient } from '../../api/formkitClient';
import { stateContentApi } from '../../api/stateContent';

/**
 * The form builder over the tenant's state-content schema — the fields every
 * state's content has — and, at `/settings/states/content/manifests/:id`, over
 * one of its manifests (a per-state table: deadlines, fee schedules, …).
 * The manifest route is reached from a manifest field's "Edit columns" link;
 * its "back" returns to the state-content builder. Same thin-host shape as
 * PartnerFormBuilder.
 */
const StateContentBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { manifestId } = useParams<{ manifestId?: string }>();
  const { data: form, isLoading } = useQuery({
    queryKey: ['state-content-form'],
    queryFn: stateContentApi.getForm,
    enabled: !manifestId,
  });

  const formId = manifestId ? Number(manifestId) : form?.id;
  if (!formId || (!manifestId && isLoading)) {
    return (
      <AdminLayout>
        <p className="sgk-admin__sub" data-testid="state-content-builder-loading">Loading…</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout fullBleed>
      <FormBuilder
        key={formId}
        client={formKitClient}
        formId={formId}
        labels={{ manifest: 'Table' }}
        onBack={() => navigate(manifestId ? '/settings/states/content' : '/settings/states')}
        backLabel={manifestId ? 'State Content' : 'States'}
        onEditManifest={(id) => navigate(`/settings/states/content/manifests/${id}`)}
      />
    </AdminLayout>
  );
};

export default StateContentBuilder;
